import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Callout } from "components/ui/callout";
import * as React from "react";
import { SearchCalloutButtons } from "./search-callout-buttons";
import { ISearchFilterClosable } from "./search-filter-closable.interface";
import {
    CalloutAlignment,
    CalloutArrowPosition,
    useDynamicCalloutPosition,
} from "./use-dynamic-callout-position";

interface SearchbarItemProps {
    content: JSX.Element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFilterSubmitted: (params: any) => void;
    children: React.ReactNode;
    align?: CalloutAlignment;
    arrowPosition?: CalloutArrowPosition;
}

interface ISearchFilterValue {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    activeState: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    defaultState: any;
}

export const SearchbarItem: React.FC<SearchbarItemProps> = props => {
    const { onFilterSubmitted, children, align, arrowPosition } = props;
    const [dynamicRef, dynamicPosition] = useDynamicCalloutPosition();

    const [state, setState] = React.useState({
        isSubmitted: false,
        isCancelled: false,
        isValid: false,
        forceClose: false,
    });

    // Use dynamic positioning if no explicit positioning is provided
    const effectiveAlign = align || dynamicPosition.align;
    const effectiveArrowPosition = arrowPosition || dynamicPosition.arrowPosition;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const isFilter = React.useCallback((child: any) => {
        return (child.type.prototype as ISearchFilterClosable).onClose !== undefined;
    }, []);

    const onFilterValidityChanged = React.useCallback((isValid: boolean) => {
        setState(prevState => ({ ...prevState, isValid }));
    }, []);

    const onFilterStateChanged = React.useCallback(
        (params: ISearchFilterValue) => {
            setState(prevState => {
                if (
                    ((prevState.isSubmitted && !prevState.isValid) ||
                        !prevState.isSubmitted) &&
                    !prevState.isCancelled
                ) {
                    return prevState;
                }

                const filterState = prevState.isCancelled
                    ? params.defaultState
                    : params.activeState;

                onFilterSubmitted(filterState);

                return { ...prevState, isSubmitted: false, isCancelled: false };
            });
        },
        [onFilterSubmitted],
    );

    const renderFilter = React.useCallback(() => {
        return React.Children.map(children, child => {
            if (isFilter(child))
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                return React.cloneElement(child as React.ReactElement<any>, {
                    onClose: onFilterStateChanged,
                    onValid: onFilterValidityChanged,
                    isSubmitted: state.isSubmitted,
                });
            else return child;
        });
    }, [
        children,
        isFilter,
        onFilterStateChanged,
        onFilterValidityChanged,
        state.isSubmitted,
    ]);

    const onSubmit = React.useCallback(() => {
        const toBeSaved = {
            isSubmitted: true,
            forceClose: false,
        };

        if (state.isValid) {
            toBeSaved.forceClose = true;
        }

        setState(prevState => ({ ...prevState, ...toBeSaved }));
    }, [state.isValid]);

    const onClear = React.useCallback(() => {
        setState(prevState => ({ ...prevState, isCancelled: true, forceClose: true }));
    }, []);

    const renderButton = React.useCallback((): JSX.Element => {
        return (
            <div className="flex items-center space-x-1 cursor-pointer text-gray-700 lg:whitespace-nowrap">
                <div className="text-lg lg:truncate">{props.content}</div>
                <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
            </div>
        );
    }, [props.content]);

    const onOpen = React.useCallback((): void => {
        setState(prevState => ({ ...prevState, forceClose: false }));
    }, []);

    return (
        <div className="searchbar-item-component" ref={dynamicRef}>
            <Callout
                renderButton={renderButton}
                onOpen={onOpen}
                forceClose={state.forceClose}
                align={effectiveAlign}
                arrowPosition={effectiveArrowPosition}
                maxWidthPx={dynamicPosition.maxWidthPx}
                mobileFullWidth={dynamicPosition.mobileFullWidth}>
                {renderFilter()}
                <SearchCalloutButtons onSubmit={onSubmit} onClear={onClear} />
            </Callout>
        </div>
    );
};
